import { SmartSendProvider } from '~/lib/consts/providers';
import { QuoteState, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { ExtendedOrderCache } from '../cache/ExtendedOrderCache';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { KeyedSmartSendGroupCache } from '../cache/SmartSendGroupCache';
import { ISmartSendContext } from '../context/SmartSendContextModels';
import { ApplierOptionsForPotentialBulkActions } from './ApplierOption';
import { SumNumber, ToNumber, onlyUnique } from './ArrayHelper';
import ItemIsNotReady from './ItemIsNotReady';
import ItemIsReady from './ItemIsReady';
import GenerateOptions from './SmartSendBulkActionOptions';
import SmartSendFilterApplier, {
  DefaultSummary,
  SmartSendFilterContext,
  SmartSendSummary
} from './SmartSendFilterApplier';
import { ToInlineCommaList } from './StringHelper';
import { SmartSendItemGroup } from '../../components/smartsend/item/SmartSendGroup';
import { DefaultFilter } from './DefaultFilter';
import { SmartSendSubType } from '../consts/SmartSendSubType';

export const filterMasterItems = (x: SmartSendItemApiModel) => x.Status !== 'Deleted';

export const IsSmartSendByType = (type: string) => type === 'SmartSend';

export const IsSmartSend = (x: SmartSendItemApiModel) => IsSmartSendByType(x.Type);

export const SmartSendHasSource = (x: SmartSendItemApiModel, source: string) =>
  x.Source.Id === source || x.MarketplaceOrderLines.find((m) => m.Source.Id === source);

export const CanMarkAsDispatched = (x: SmartSendItemApiModel): boolean =>
  x.MarketplaceStatus == 'AwaitingDispatch' && HasExternalTrackingNumber(x);

export const HasExternalTrackingNumber = (x: SmartSendItemApiModel): boolean => !!GetExternalTrackingNumber(x);

export const GetExternalTrackingNumber = (x: SmartSendItemApiModel): string => x?.Meta['ExternalTrackingNumber']?.Value;

export const HasClaims = (item: SmartSendItemApiModel) =>
  !!ExtendedOrderCache.claimOrderLineIds.find((x) => x.orderlineId === item.BookingDetails?.OrderLineId);

export const IsInternational = (item: SmartSendItemApiModel) =>
  item.Recipient?.CountryIsoCode != item?.Sender?.CountryIsoCode;

export const IsSavedForLater = (item: SmartSendItemApiModel) =>
  item.MarketplaceStatus === 'Sold' && item.Status == 'Inactive';

export const IsDomestic = (item: SmartSendItemApiModel) =>
  item.Recipient?.CountryIsoCode === item.Sender?.CountryIsoCode;

export const IsSelected = (item: SmartSendItemApiModel) => item.State?.selected === true;

export const HasRefund = (item: SmartSendItemApiModel) =>
  !!ExtendedOrderCache.refundOrderLineIds.find((x) => x.orderlineId === item.BookingDetails?.OrderLineId);

export const CanGenerateLabel = (x: SmartSendItemApiModel) =>
  SettingsCache.settings.Features.includes('Labels') &&
  x.BookingDetails?.Service?.Provider === SmartSendProvider &&
  x.BookingDetails?.OrderLineId;

export const IsReturnCollection = (item: SmartSendItemApiModel) => item.Tags?.includes('ReturnCollection');

export const ItemShouldAllowCustomerUpdate = (item: SmartSendItemApiModel) =>
  item.Status == 'Active' && item.MarketplaceStatus == 'Sold' && !IsReturnCollection(item);

const deliveryTypes = ['Shop', 'Locker'];
export const isDeliveryToShopService = (item: SmartSendItemApiModel) =>
  item.State?.validation?.QuoteState?.Quote?.Service?.PickUpProviderCode &&
  deliveryTypes.includes(item.State.validation.QuoteState.Quote.Service.DeliveryType);

export const GetQuoteServiceTagsFromItem = (item: SmartSendItemApiModel): string[] =>
  item?.State?.validation?.QuoteState?.Quote?.Service?.Tags || [];

export const GetQuoteServiceTags = (x: QuoteState): string[] => x?.Quote?.Service?.Tags || [];

export const ServiceSupportsMultiParcelShipment = (x: QuoteState): boolean =>
  GetQuoteServiceTags(x).includes('MultiConsignment');

export const ItemServiceSupportsMultiParcelShipment = (x: SmartSendItemApiModel): boolean =>
  ServiceSupportsMultiParcelShipment(x.State?.validation?.QuoteState);

export const GetItemServiceTags = (x: SmartSendItemApiModel): string[] =>
  GetQuoteServiceTags(x.State?.validation?.QuoteState);

export const GetItemWeight = (x: SmartSendItemApiModel) =>
  Number(
    (x.Consignment?.Parcels?.length > 0
      ? SumNumber(x.Consignment.Parcels.map((p) => p.Dimensions.Weight))
      : ToNumber(x.PackageDimensions.Weight)
    ).toFixed(2)
  );

export const IsAwaitingDispatch = (x: SmartSendItemApiModel) =>
  IsSmartSend(x) &&
  x.Status === 'Active' &&
  x.MarketplaceStatus === 'AwaitingDispatch' &&
  !x.TrackingSummary?.DroppedOff &&
  !x.TrackingSummary?.Collected;

export const ToItemSource = (x: { Source: { Name: string }; Type?: string }) => {
  if (IsSmartSendByType(x.Type)) {
    return x.Source.Name;
  }
  switch (x.Type) {
    case 'BulkUpload':
    case 'AutoSend':
      return 'Legacy Smart Send';
    case 'OrderProcess':
    case 'OrderProcess:V2.0':
    case 'Reorder':
      return 'Order Process';
    default:
      return 'Non-Smart Send';
  }
};

export const ToItemSources = (x: SmartSendItemApiModel) =>
  ToInlineCommaList(x.MarketplaceOrderLines.map((m) => m.Source.Name).filter(onlyUnique));

export default (
  state: ISmartSendContext,
  soldItemsFilter: SmartSendFilterContext,
  resetSelection: boolean,
  smartSendItemsCache: SmartSendItemApiModel[]
): ISmartSendContext => {
  for (const key in state.filters) {
    const res = SmartSendSummaryGeneratorActual(
      key,
      resetSelection,
      smartSendItemsCache,
      key === SmartSendSubType ? soldItemsFilter : state.filters[key].filter
    );
    state.filters[key].summary = res.summary;
    state.filters[key].filter = res.filter;
    KeyedSmartSendGroupCache[key] = res.groups;
  }

  return state;
};

export const SmartSendSummaryGeneratorActual = (
  key: string,
  resetSelection: boolean,
  smartSendItemsCache: SmartSendItemApiModel[],
  filter?: SmartSendFilterContext
): { summary: SmartSendSummary; filter: SmartSendFilterContext; groups: SmartSendItemGroup[] } => {
  const newFilter: SmartSendFilterContext = filter ? { ...filter } : { ...DefaultFilter() };

  const items = smartSendItemsCache.filter(filterMasterItems);
  const groups = SmartSendFilterApplier(items, newFilter);

  const newSummary: SmartSendSummary = { ...DefaultSummary() };
  newSummary.count = 0;
  newSummary.ready = 0;
  newSummary.notReady = 0;
  newSummary.savedForLater = 0;
  newSummary.selected = [];
  newSummary.groupCount = groups.length;
  newSummary.bulkOptions = [];
  newSummary.potentialBulkOptions = [];
  newSummary.states = [];

  const baseStats = ['Sold', 'Ready', 'SavedForLater', 'NotReady', 'Groupable'];
  baseStats.forEach((s) => {
    if (!newSummary.stats.find((b) => b.key == s)) {
      newSummary.stats.push({ key: s, value: 0 });
    }
  });

  newSummary.stats.forEach((s) => {
    switch (s.key) {
      case 'Sold':
        s.value = items.filter(IsSmartSend).filter((x) => x.MarketplaceStatus === 'Sold').length;
        break;
      case 'Ready':
        s.value = items.filter(IsSmartSend).filter(ItemIsReady).length;
        break;
      case 'SavedForLater':
        s.value = items.filter(IsSmartSend).filter(IsSavedForLater).length;
        break;
      case 'NotReady':
        s.value = items.filter(IsSmartSend).filter(ItemIsNotReady).length;
        break;
      case 'Groupable':
        {
          let cnt = 0;
          const keyStore = {};
          items
            .filter((i) => IsSmartSend(i) && i.Status === 'Active' && i.MarketplaceStatus === 'Sold')
            .map((i) => i.PropertyKey)
            .forEach((x) => {
              if (keyStore[x]) {
                cnt++;
              } else {
                keyStore[x] = true;
              }
            });
          s.value = cnt;
        }
        break;
    }
  });

  groups.forEach((g) => {
    g.Items.forEach((i) => {
      const itemOptions = GenerateOptions(i, i.State?.validation);
      if (resetSelection) {
        i.State.selected = false;
      } else {
        newSummary.count++;
        if (IsSelected(i)) {
          newSummary.selected.push(i.Id);
          newSummary.bulkOptions = newSummary.bulkOptions.concat(itemOptions);
        }
      }

      newSummary.potentialBulkOptions = newSummary.potentialBulkOptions
        .concat(itemOptions)
        .filter((o) => ApplierOptionsForPotentialBulkActions.includes(o));

      if (ItemIsReady(i)) {
        newSummary.ready++;
      }
      if (ItemIsNotReady(i)) {
        newSummary.notReady++;
      }
      if (IsSavedForLater(i)) {
        newSummary.savedForLater++;
      }
      if (IsAwaitingDispatch(i)) {
        newSummary.awaitingDispatch++;
      }
    });
  });

  groups.forEach((g) => {
    newSummary.states = newSummary.states.concat(g.States).filter(onlyUnique);
  });

  let indexToShowFrom = newFilter.CurrentFilter.Skip;
  if (indexToShowFrom > newSummary.count) {
    // If they are filtering and the paging is greater than that which can be returned, reset.
    newFilter.CurrentFilter.Skip = 0;
    indexToShowFrom = 0;
  }
  const indexToShowTo = indexToShowFrom + newFilter.CurrentFilter.Take;

  const hidden = [];

  if (key === SmartSendSubType) {
    // Show and Hide based on item index
    // SmartSend Is Special as its the only one which holds the full list of items and does paging client side
    let index = 0;
    groups.forEach((g) => {
      const hiddenResults: boolean[] = [];
      g.Items.forEach((i) => {
        const itemIsHidden = index < indexToShowFrom || index >= indexToShowTo;
        if (itemIsHidden) {
          hidden.push(i.Id);
          hiddenResults.push(itemIsHidden);
        }
        index++;
      });
      g.Hidden = g.Items.length === hiddenResults.length;
    });
  }

  const visibleGroups = groups
    .filter((x) => !x.Hidden)
    .map((x) => {
      return { ...x, Items: x.Items.filter((i) => !hidden.includes(i.Id)) };
    });

  return { summary: newSummary, filter: newFilter, groups: visibleGroups };
};
